// vendor.ts -- Immutable vendor libraries, cached separately from app.js.
// Only changes when dependencies change (npm install).

// Step 1: jQuery globals (MUST be first -- head.htm guard accepts first assignment)
import './jquery-bootstrap';

// Step 2: Value imports
import FileSaver from 'file-saver';
import base64js from 'base64-js';
// @ts-ignore
import Popper from 'popper.js/dist/umd/popper.js';
// @ts-ignore
import Chart from 'chart.js/auto';
// @ts-ignore
import ChartDataLabels from 'chartjs-plugin-datalabels';
// @ts-ignore
import { Dropzone } from 'dropzone';
// @ts-ignore
import bootbox from 'bootbox';
import Swal from 'sweetalert2';
// SweetAlert2 ships its own stylesheet, and this is the first CSS vendor.ts has ever imported.
// Verified against the built output 2026-09-14: because this entry is built as an `iife` with no
// HTML entry, Vite does NOT emit a separate .css file — it inlines the stylesheet into vendor.js
// and injects it at runtime via document.createElement('style'). So there is nothing to reference
// from head.htm and no build-config change is needed.
//
// The consequence that matters: the rules arrive in a <style> appended at runtime, so any override
// of ours must win on specificity rather than on source order. The theme overrides are written
// accordingly (see _sweetalert2.scss).
import 'sweetalert2/dist/sweetalert2.min.css';
// @ts-ignore
import html2canvas from 'html2canvas';
// @ts-ignore
import tempStorage from 'expiry-localstorage';

declare global {
    interface Window {
        jQuery: JQuery;
        $: JQuery;
        Base64: typeof base64js;
        FileSaver: typeof FileSaver;
        [key: string]: any;
    }
}

// Step 3: Window global assignments
Chart.register(ChartDataLabels);
window.Chart = Chart;
window.Popper = Popper;
window.dropzone = Dropzone;
window.Base64 = base64js;
window.FileSaver = FileSaver;
window.bootbox = bootbox;
// Expose SweetAlert2 as `Swal` and NEVER as `swal`.
//
// Measured 2026-09-14 on a live BT page: SweetAlert2's UMD build overwrites `window.swal`, which
// is SweetAlert 1 wherever october-min.js is loaded. The old guard `typeof window.swal ===
// 'function'` still passes, the SweetAlert-1-style call `swal(options, callback)` then throws
// `TypeError: Class constructor On cannot be invoked without 'new'`, the promise rejects, and
// because every call site uses .then() without .catch() the rejection is never handled — the user
// clicks « Supprimer » and nothing at all happens. No dialog, no error, no deletion.
//
// This ES import does not touch `window.swal`. Keep it that way: assigning it here, or loading
// SweetAlert2's UMD build from a CDN on any page, reintroduces that silent failure.
window.Swal = Swal;
window.html2canvas = html2canvas;
window.tempStorage = tempStorage;

// Step 4: jQuery plugins (register on $.fn via side effects)
import 'bootstrap';
import 'jquery-ui/ui/version';
import 'jquery-ui/ui/widget';
import 'jquery-ui/ui/data';
import 'jquery-ui/ui/scroll-parent';
import 'jquery-ui/ui/widgets/mouse';
import 'jquery-ui/ui/widgets/sortable';
import 'jquery-ui/ui/widgets/selectable';
import 'jquery-contextmenu';
import 'jquery-validation';
import 'dragtable';
import 'bootstrap-select';
import 'bootstrap-toggle';
import 'bootstrap-tagsinput';
import 'typeahead.js';

// Step 5: Vendor-level configuration
bootbox.setDefaults({ locale: 'fr' });
